import axios from 'axios'
import { ResponseAPIWithPagination } from '../../../common/types/API.types'
import { Luminaire, LuminaireEffectiveConfig, LuminaireMetric, LuminaireMetricFilter } from '../types/luminaire.types'
import { Point } from '../../Jouly/types/types'

const BACKEND_URL = process.env.REACT_APP_BACKEND_API_URL + '/api/v1'

export const getAllLuminaires = async (
  page: number,
  pageSize: number,
  search?: string,
  tags?: string[],
): Promise<ResponseAPIWithPagination<Luminaire>> => {
  const params = new URLSearchParams({
    currentPage: page.toString(),
    pageSize: pageSize.toString(),
  })
  if (search) params.append('query', search)
  if (tags && tags.length > 0) params.append('tags', tags.join(','))
  return (
    await axios.get<ResponseAPIWithPagination<Luminaire>>(
      `${BACKEND_URL}/luminaires?${params.toString()}`
    )
  ).data
}

export const getLuminaireMetric = async (
  metric: LuminaireMetric,
  filter: LuminaireMetricFilter
): Promise<Point[]> => {
  // Mode "dernière valeur" : le backend gère lui-même la fenêtre (-30d par
  // défaut, fallback glissant jusqu'à 1 an). On omet range/aggregateWindow
  // pour ne pas pinner explicitement la fenêtre côté caller (sinon le
  // backend désactive le fallback — opt-out volontaire).
  const params = filter.last
    ? `last=true`
    : `range=start=${filter.startDate},stop=${filter.stopDate}&aggregateWindow=${filter.aggregateWindow}`
  return (
    await axios.get<Point[]>(
      `${BACKEND_URL}/analytics/luminaires/${filter.devEui}/${metric}?${params}`
    )
  ).data
}

export const getLuminaireById = async (lumId: string): Promise<Luminaire> => {
  return (await axios.get<Luminaire>(`${BACKEND_URL}/luminaires/${lumId}`)).data
}

export const createLuminaire = async (
  luminaire: Omit<Luminaire, 'lum_id'>
): Promise<Luminaire> => {
  return (await axios.post<Luminaire>(`${BACKEND_URL}/luminaires`, luminaire)).data
}

export const updateLuminaire = async (
  lumId: string,
  luminaire: Luminaire
): Promise<Luminaire> => {
  return (await axios.put<Luminaire>(`${BACKEND_URL}/luminaires/${lumId}`, luminaire)).data
}

export const deleteLuminaire = async (lumId: string): Promise<null> => {
  return (await axios.delete<null>(`${BACKEND_URL}/luminaires/${lumId}`)).data
}

export const getEffectiveConfig = async (devEui: string): Promise<LuminaireEffectiveConfig> =>
  (await axios.get<LuminaireEffectiveConfig>(`${BACKEND_URL}/analytics/luminaires/${devEui}/effective-config`)).data

const luminaireAPI = {
  getAllLuminaires,
  getLuminaireById,
  getLuminaireMetric,
  createLuminaire,
  updateLuminaire,
  deleteLuminaire,
  getEffectiveConfig,
}

export default luminaireAPI
